import type {
    ColourType,
    ExtraType,
    PackType,
    UpholsteryType,
    WheelType,
} from "../../../../common-deprecated/types/CommonTypes";
import type { ImageCarConfigType, ImageCarType } from "../../../../common-deprecated/settings/utils/ccisSettingUtils";
import type { CommonSettingsType } from "../../../../common-deprecated/settings/fetchCommonSettings";
import { getCarDBAssetUrl } from "../../../shared-logic/utils/imageUtils";
import type { CarType } from "../../../shared-logic/types/CarType";
import type { CarConfigMotorizationType } from "../redux/slices/CarConfigMotorizationSlice";
import { getCarImage } from "../../shared-logic/utils/imageUtils";
import { isRetailer } from "../../../../common-deprecated/utils";
import { TOYOTA } from "../../../../common-deprecated/constants";

export const getColorVisual = (
    colour: ColourType,
    commonSettings: CommonSettingsType,
    // the carIds and carImageInput functionality is only used in non retailer hence they are optional
    carIds?: ImageCarType,
    carImageInput?: ImageCarConfigType,
): string => {
    const isRetailerToyota = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;

    if (isRetailerToyota || !carIds) {
        const url = getCarDBAssetUrl(colour.image, commonSettings.compressedCcisPath, 200, 200);
        return url;
    } else {
        const carImage = getCarImage(
            commonSettings,
            18,
            carIds,
            {
                type: "png",
                width: 640,
                height: 640,
            },
            carImageInput,
        ).src;

        return carImage;
    }
};

export const getColorBackgroundVisual = (
    colour: ColourType,
    commonSettings: CommonSettingsType,
    // the carIds and carImageInput functionality is only used in non retailer hence they are optional
    carIds?: ImageCarType,
): string => {
    const isRetailerToyota = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;
    const bgColor = colour.rgb;
    const fallback = bgColor.substring(0, 2) === "0x" ? bgColor.substring(2) : bgColor;

    if (isRetailerToyota || !carIds) {
        const url = getCarDBAssetUrl(colour.image, commonSettings.compressedCcisPath, 200, 200);
        return `url('${url}') center/cover no-repeat, #${fallback}`;
    } else {
        const url = getCarDBAssetUrl(colour.image, commonSettings.compressedCcisPath, 400, 400);
        return `url('${url}') center/cover no-repeat, #${fallback}`;
    }
};

export const getEngineVisual = (
    car: { initialized: boolean } & CarType,
    carConfigMotorization: CarConfigMotorizationType,
    commonSettings: CommonSettingsType,
): string | undefined => {
    const foundEngine = carConfigMotorization.motorization.find(({ carId }) => carId === car.id)?.engine;

    if (foundEngine && foundEngine.image) {
        let size = 1200;
        const isToyotaRetailer = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;
        if (isToyotaRetailer) size = 200;
        const url = getCarDBAssetUrl(foundEngine.image, commonSettings.compressedCcisPath, size, size);
        return url;
    }

    return undefined;
};

const findPackExtraImage = (packs: PackType[], extras: ExtraType[]): string | null => {
    if (extras.length < 0 && packs.length < 0) return null;

    const packWithImage = packs.find((pack) => pack.image !== null && pack.image !== "");
    if (packWithImage?.image) return packWithImage.image;

    let packEquipmentWithImage: WheelType | ExtraType | undefined;
    packs.forEach((pack) => {
        if (!packEquipmentWithImage) {
            pack.equipment.forEach((equipment) => {
                if (!packEquipmentWithImage) {
                    if (equipment.image !== null && equipment.image !== "") {
                        packEquipmentWithImage = equipment;
                    }
                }
            });
        }
    });
    if (packEquipmentWithImage?.image) return packEquipmentWithImage.image;

    const extraWithImage = extras.find((extra) => !!extra.image);
    if (extraWithImage?.image) return extraWithImage.image;

    return null;
};

export const getPacksVisual = (
    basketPacks: PackType[],
    basketExtras: ExtraType[],
    commonSettings: CommonSettingsType,
    // the possiblePacks and extras functionality is only used in retailer hence they are optional
    possiblePacks?: PackType[],
    possibleExtras?: ExtraType[],
): string | undefined => {
    let image = findPackExtraImage(basketPacks, basketExtras);

    if (!image && possiblePacks && possibleExtras) {
        image = findPackExtraImage(possiblePacks, possibleExtras);
    }
    if (image) {
        let size = 400;
        const isToyotaRetailer = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;
        if (isToyotaRetailer) size = 200;
        const url = getCarDBAssetUrl(image, commonSettings.compressedCcisPath, size, size);
        return url;
    }

    return undefined;
};

export const getUpholsteryVisual = (
    upholstery: UpholsteryType,
    commonSettings: CommonSettingsType,
): string | undefined => {
    let size = 1200;
    const isToyotaRetailer = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;
    if (isToyotaRetailer) size = 200;
    const url = getCarDBAssetUrl(upholstery.image, commonSettings.compressedCcisPath, size, size);
    return url;
};

export const getWheelsVisual = (wheel: WheelType, commonSettings: CommonSettingsType): string => {
    const isToyotaRetailer = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;

    if (isToyotaRetailer) {
        const url = getCarDBAssetUrl(wheel.image, commonSettings.compressedCcisPath, 200, 200);
        return url;
    }
    const url = getCarDBAssetUrl(wheel.image, commonSettings.compressedCcisPath, 1200, 1200);
    return url;
};

export const getChargingVisual = (
    homeChargers: ExtraType[],
    commonSettings: CommonSettingsType,
): string | undefined => {
    const isToyotaRetailer = isRetailer(commonSettings) && commonSettings.brand === TOYOTA;

    if (homeChargers.length !== 0) {
        const homeChargerWithImage = homeChargers.find(
            (homeCharger) => homeCharger.image !== null && homeCharger.image !== "",
        );
        let size = 1200;
        if (homeChargerWithImage) {
            if (isToyotaRetailer) size = 200;
            const url = getCarDBAssetUrl(homeChargerWithImage?.image, commonSettings.compressedCcisPath, size, size);

            return url;
        }
    }
    return undefined;
};
